import { useTranslation } from 'react-i18next';
import { useRef, useEffect } from 'react';
import { SparkleIcon, StopIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import {
    FilterBar,
    AttachmentButton,
    PromptInput,
    usePromptSuggestion,
} from '@/components';
import { QueryAttachmentRow } from '@/features/QueryPanel/components/QueryAttachmentRow';
import { RecentQueriesDropdown } from '@/features/QueryPanel/components/RecentQueriesDropdown';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { ALERT_PROMPT_PREFIX } from '@/constants/alert';
import { useQueryAttachment } from '@/features/QueryPanel/hooks/useQueryAttachment';
import { useRecentQueries } from '@/features/QueryPanel/hooks/useRecentQueries';
import { deriveAttachmentDisplay } from '@/features/QueryPanel/utils/attachmentDisplay';

interface PromptDefinitionStepProps {
    prompt: string;
    onPromptChange: (prompt: string) => void;
    attachment?: { url: string; type?: string };
    onAttachmentChange: (attachment?: { url: string; type?: string }) => void;
    selectedSourceIds: string[];
    onRemoveSource: (id: string) => void;
    onNext: () => void;
    onRunSimulation: () => void;
    onStopSimulation: () => void;
    isSimulating: boolean;
    error?: string;
}

export function PromptDefinitionStep({
    prompt,
    onPromptChange,
    attachment,
    onAttachmentChange,
    selectedSourceIds,
    onRemoveSource,
    onNext,
    onRunSimulation,
    onStopSimulation,
    isSimulating,
    error,
}: PromptDefinitionStepProps) {
    const { t } = useTranslation(I18nNamespace.ALERT_RULES);
    const {
        attachmentFile,
        isUploading,
        uploadingFileName,
        uploadProgress,
        uploadPreviewUrl,
        handleAttach,
        handleRemove,
    } = useQueryAttachment();
    const { recentQueries, removeRecentQuery, clearRecentQueries } =
        useRecentQueries();
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const suggestion = usePromptSuggestion({
        prompt,
        onPromptChange,
        inputRef,
    });
    const hasPromptContent = !!prompt.trim() || !!attachment;
    const canProceed = hasPromptContent && !suggestion.isLoadingSuggestion;

    const handleSelectRecentQuery = (query: string) => {
        onPromptChange(query);
        inputRef.current?.focus();
    };

    useEffect(() => {
        if (attachmentFile && !isUploading) {
            if (attachment?.url !== attachmentFile.url) {
                onAttachmentChange({ url: attachmentFile.url });
            }
        }
    }, [attachmentFile, isUploading, attachment?.url, onAttachmentChange]);

    const handleRemoveAttachment = () => {
        handleRemove();
        onAttachmentChange(undefined);
    };

    const { imageSrc, label, showRow } = deriveAttachmentDisplay({
        attachmentFile,
        uploadPreviewUrl,
        uploadingFileName,
        fallbackUrl: attachment?.url,
    });

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col rounded-[10px] border border-grey-500 bg-grey-600 shadow-[4px_4px_48px_rgb(7_8_10)]">
                {showRow && (
                    <QueryAttachmentRow
                        imageSrc={imageSrc!}
                        fileName={label}
                        isUploading={isUploading}
                        uploadProgress={uploadProgress}
                        onRemove={handleRemoveAttachment}
                    />
                )}
                <FilterBar
                    className={cn('px-3 pb-2', showRow ? 'pt-1' : 'pt-3')}
                    sourceIds={selectedSourceIds}
                    onRemoveSource={onRemoveSource}
                />
                <div className="px-3 pb-2 pt-3">
                    <PromptInput
                        value={prompt}
                        onValueChange={onPromptChange}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                        }}
                        inputRef={inputRef}
                        prefix={ALERT_PROMPT_PREFIX}
                        showPlaceholder={false}
                        placeholder={t('form.promptDefinition.placeholder')}
                        completions={recentQueries}
                        suggestion={suggestion}
                    />
                    <div className="mt-2 flex items-center gap-1.5">
                        <AttachmentButton
                            onUpload={(file) => handleAttach({ file })}
                            disabled={!!attachmentFile || !!attachment}
                        />
                        <RecentQueriesDropdown
                            queries={recentQueries}
                            onSelect={handleSelectRecentQuery}
                            onRemove={removeRecentQuery}
                            onClearAll={clearRecentQueries}
                            label={t('form.promptDefinition.recentQueries')}
                            emptyLabel={t(
                                'form.promptDefinition.noRecentQueries',
                            )}
                            removeLabel={t(
                                'form.promptDefinition.removeQuery',
                                { defaultValue: 'Remove' },
                            )}
                            clearLabel={t('form.promptDefinition.clearAll')}
                            triggerClassName="h-8 gap-1.5 px-1.5 text-grey-200 hover:text-white-100"
                        />
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-end gap-3">
                <Button
                    disabled={!isSimulating && !canProceed}
                    variant="secondary"
                    onClick={isSimulating ? onStopSimulation : onRunSimulation}
                    className="flex-shrink-0"
                >
                    <span className="flex px-1 items-center gap-2">
                        {isSimulating ? (
                            <StopIcon
                                size={16}
                                weight="fill"
                                className="text-white-100"
                            />
                        ) : (
                            <SparkleIcon
                                size={16}
                                className={cn(
                                    'text-white-100',
                                    !canProceed && 'text-grey-300',
                                )}
                            />
                        )}
                        {isSimulating
                            ? t('form.promptDefinition.stopSimulation')
                            : t('form.promptDefinition.runSimulation')}
                    </span>
                </Button>
                <Button onClick={onNext} disabled={!canProceed}>
                    {t('form.buttons.next')}
                </Button>
            </div>
            {error && <p className="text-sm text-red-400">{error}</p>}
        </div>
    );
}
